import { useMemo, useState } from 'react'
import {
  Button,
  Card,
  DataTable,
  DEFAULT_PAGE_SIZE,
  Field,
  Input,
  Pager,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import {
  loadTransferStatementAll,
  useTransferStatement,
} from '@/api/hooks/reports/useTransferStatement'
import {
  LAB_TRANSFER_STATEMENT_MAX_DAYS,
  transferStatementRangeDays,
  type TransferStatementRow,
} from '@/api/map/transferStatement'
import { Money, exportCsv } from '@/app/bits'
import { CmPanel } from '@/features/catalog/CmPanel'
import { EMPTY, count, isoDay, lll } from '@/lib/format'
import '@/features/bet-ticker/bet-ticker.css'
import '@/features/member/member.css'

type Applied = { from: string; to: string; run: number }

/**
 * Account Statement → Transfer Statement — Lab `viewTransactionNew`.
 *
 * CM-UI → CM-API `POST /v1/transfer-statement/search` (shared ledger).
 * No Agent-API / hierarchySelf / analytics proxy.
 */
export function TransferStatementPage() {
  const toast = useToast()
  const today = isoDay(new Date())

  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [page, setPage] = useState(1)
  const [applied, setApplied] = useState<Applied>({
    from: today,
    to: today,
    run: 1,
  })
  const [generating, setGenerating] = useState(false)

  const pageSize = DEFAULT_PAGE_SIZE
  const rangeLabel = `${applied.from} – ${applied.to}`

  const { data, isLoading, isFetching, isError, error } = useTransferStatement({
    from: applied.from,
    to: applied.to,
    page,
    pageSize,
    run: applied.run,
  })

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const viewerName = data?.viewer.username || 'you'
  const currencyType = data?.viewer.currencyType || 'INR'
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  const columns: Column<TransferStatementRow>[] = useMemo(
    () => [
      {
        id: 'at',
        header: 'Creation Date',
        width: 180,
        cell: (t) => lll(t.createdAt),
        sortValue: (t) => t.createdAt,
      },
      {
        id: 'from',
        header: 'From user',
        cell: (t) => t.fromUser.username || EMPTY,
        sortValue: (t) => t.fromUser.username || '',
      },
      {
        id: 'to',
        header: 'To user',
        cell: (t) => t.toUser.username || EMPTY,
        sortValue: (t) => t.toUser.username || '',
      },
      {
        id: 'amount',
        header: 'Amount',
        numeric: true,
        cell: (t) => (
          <Money
            value={t.amount}
            currency={currencyType}
            tone={t.transactionType === 'Taken' ? 'profit' : 'loss'}
          />
        ),
        sortValue: (t) => t.amount,
      },
      {
        id: 'type',
        header: 'Transaction Type',
        width: 130,
        cell: (t) => t.transactionType,
        sortValue: (t) => t.transactionType,
      },
      {
        id: 'comments',
        header: 'Comments',
        cell: (t) => t.comments?.trim() || EMPTY,
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (t) => t.ip?.trim() || EMPTY,
      },
    ],
    [currencyType],
  )

  const runSearch = () => {
    const days = transferStatementRangeDays(fromDate, toDate)
    if (days > LAB_TRANSFER_STATEMENT_MAX_DAYS) {
      toast.error(
        'Date range too wide',
        `The date you have selected exceeds ${LAB_TRANSFER_STATEMENT_MAX_DAYS} days. Please try again.`,
      )
      return
    }
    if (!fromDate || !toDate) {
      toast.error('Dates required', 'Provide From and To Date')
      return
    }
    setPage(1)
    setApplied((prev) => ({ from: fromDate, to: toDate, run: prev.run + 1 }))
  }

  const runGenerate = async () => {
    const days = transferStatementRangeDays(fromDate, toDate)
    if (days > LAB_TRANSFER_STATEMENT_MAX_DAYS) {
      toast.error(
        'Date range too wide',
        `The date you have selected exceeds ${LAB_TRANSFER_STATEMENT_MAX_DAYS} days. Please try again.`,
      )
      return
    }
    setGenerating(true)
    try {
      const all = await loadTransferStatementAll(fromDate, toDate)
      if (!all.rows.length) {
        toast.error('No data', 'No transfers in this range to generate.')
        return
      }
      exportCsv(
        `transfer-statement_${fromDate}_${toDate}.csv`,
        all.rows.map((t) => ({
          'Creation Date': lll(t.createdAt),
          'From user': t.fromUser.username,
          'To user': t.toUser.username,
          Amount: t.amount,
          'Transaction Type': t.transactionType,
          Comments: t.comments ?? '',
          IP: t.ip ?? '',
        })),
      )
      toast.success('Generated', `${count(all.rows.length)} rows exported.`)
    } catch (err) {
      toast.error(
        'Generate failed',
        err instanceof Error ? err.message : 'Unable to generate transfer statement',
      )
    } finally {
      setGenerating(false)
    }
  }

  return (
    <CmPanel
      title="Transfer Statement"
      subtitle={`List of transactions for ${viewerName} · Lab viewTransactionNew`}
      wide
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
        <div className="w7-filterbar" role="region" aria-label="Transfer statement filters">
          <div className="w7-transfer-filter-row">
            <Field label="From Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="To Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              )}
            </Field>
            <Button type="button" variant="primary" busy={isFetching} onClick={runSearch}>
              Search
            </Button>
            <Button
              type="button"
              variant="secondary"
              busy={generating}
              onClick={() => void runGenerate()}
            >
              Generate
            </Button>
            <div className="w7-muted" style={{ fontSize: 'var(--w7-fs-sm)', alignSelf: 'center' }}>
              Total : {count(total)}
            </div>
          </div>
        </div>

        {isError ? (
          <StateBlock icon="warning" title="Unable to load">
            {error instanceof Error ? error.message : 'Transfer statement request failed.'}
          </StateBlock>
        ) : (
          <Card
            flush
            title="List of Transactions"
            subtitle={rangeLabel}
            footer={
              <Pager
                page={page}
                pageCount={pageCount}
                onChange={setPage}
                total={total}
                pageSize={pageSize}
              />
            }
          >
            <DataTable
              label="Transfer statement"
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={isLoading}
              skeletonRows={8}
              empty={
                <div data-testid="transfer-statement-empty">
                  <StateBlock icon="transfers" title="No Data Found">
                    No credit transfers between {rangeLabel}.
                  </StateBlock>
                </div>
              }
            />
          </Card>
        )}
      </div>
    </CmPanel>
  )
}
